const MOBILE_BREAKPOINT_PX = 1025;

const STICKY_DIR_THRESHOLD_PX = 4; // px, absorbs trackpad noise so the state can't flap

class HorizontalScroll extends HTMLElement {
  private scrollStarted: boolean = false;
  private hideScrollClass = 'hide-scroll';
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private boundHandleScroll = () => this.handleScroll();
  private boundHandleResize = () => this.updateState();
  private boundScrollPrev = () => this.scrollByVisibleItems(-1);
  private boundScrollNext = () => this.scrollByVisibleItems(1);
  private stickyCol: HTMLElement | null = null;
  private stickySpacer: HTMLElement | null = null;
  private stickyLastY = 0;
  private stickyDir = 1;
  private stickyPending = 0;
  private boundHandleStickyScroll = () => this.handleStickyScroll();

  private isMobile(): boolean {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;
  }

  connectedCallback() {
    this.addEventListener('scroll', this.boundHandleScroll, { passive: true });

    // Find navigation buttons in parent container
    const parent = this.parentElement;
    if (parent) {
      this.prevButton = parent.querySelector('[data-carousel-prev]');
      this.nextButton = parent.querySelector('[data-carousel-next]');

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.boundScrollPrev);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.boundScrollNext);
      }
    }

    // Setup ResizeObserver to handle size changes (e.g. image loading)
    this.resizeObserver = new ResizeObserver(this.boundHandleResize);

    // Observe the component itself and its first child (content wrapper)
    this.resizeObserver.observe(this);
    if (this.firstElementChild) {
      this.resizeObserver.observe(this.firstElementChild);
    }

    // Initial check
    this.updateState();

    // Re-position on window resize (fallback)
    window.addEventListener('resize', this.boundHandleResize);

    // PDP: only enabled via data-sticky-follow, other horizontal-scroll
    // usages are unaffected.
    if (this.hasAttribute('data-sticky-follow')) {
      this.stickyCol = this.parentElement;
      this.stickySpacer = this.stickyCol?.querySelector('[data-sticky-spacer]') ?? null;
      window.addEventListener('scroll', this.boundHandleStickyScroll, { passive: true });
      this.resetSticky();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.boundHandleScroll);
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.boundScrollPrev);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.boundScrollNext);
    }
    window.removeEventListener('resize', this.boundHandleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.hasAttribute('data-sticky-follow')) {
      window.removeEventListener('scroll', this.boundHandleStickyScroll);
      if (this.stickySpacer) this.stickySpacer.style.height = '0px';
      this.style.top = '';
      this.style.bottom = '';
    }
  }

  private updateState() {
    requestAnimationFrame(() => {
      this.positionButtonsRelativeToProductCard();
      this.updateButtonVisibility();
      // Column heights decide where the gallery stops, and they keep
      // changing after load - fonts settle, images load, blocks expand.
      // Re-measure whenever they do (the ResizeObserver above already
      // routes here), otherwise the stops are computed from stale heights.
      if (this.hasAttribute('data-sticky-follow')) {
        this.resetSticky();
      }
      // Mark buttons as ready (removes opacity-0)
      if (this.prevButton) this.prevButton.setAttribute('data-ready', '');
      if (this.nextButton) this.nextButton.setAttribute('data-ready', '');
    });
  }

  private resetSticky() {
    if (!this.stickyCol || !this.stickySpacer) return;

    this.stickySpacer.style.height = '0px';
    this.style.top = 'auto';
    this.style.bottom = 'auto';

    this.stickyLastY = window.scrollY;
    this.stickyDir = 1;
    this.stickyPending = 0;

    this.applyStickyDirection();
  }

  private handleStickyScroll() {
    const y = window.scrollY;
    const dy = y - this.stickyLastY;
    if (!dy) return;
    this.stickyLastY = y;

    // Back at the very top: clear any frozen offset. Without this, a spacer
    // left over from the last direction change keeps pushing the gallery
    // down, so it sits below the fold on a page that's scrolled to zero -
    // which is what you'd get on a reload, or any jump back to the top.
    if (y <= 0) {
      this.resetSticky();
      return;
    }

    const next = dy > 0 ? 1 : -1;
    if (next === this.stickyDir) {
      this.stickyPending = 0;
      return;
    }

    this.stickyPending += Math.abs(dy);
    if (this.stickyPending < STICKY_DIR_THRESHOLD_PX) return;

    this.stickyPending = 0;
    this.stickyDir = next;
    this.applyStickyDirection();
  }

  private applyStickyDirection() {
    if (!this.stickyCol || !this.stickySpacer || this.isMobile()) {
      if (this.stickySpacer) this.stickySpacer.style.height = '0px';
      this.style.top = '';
      this.style.bottom = '';
      return;
    }

    const y = window.scrollY;
    const vh = window.innerHeight;

    const colRect = this.stickyCol.getBoundingClientRect();
    const colTop = colRect.top + y;
    const colHeight = colRect.height;
    const rect = this.getBoundingClientRect();
    const h = rect.height;

    if (h <= vh) {
      this.stickySpacer.style.height = '0px';
      this.style.top = '0px';
      this.style.bottom = 'auto';
      return;
    }

    // 1. Freeze the current position into the flow.
    const frozen = Math.round(Math.min(Math.max(rect.top + y - colTop, 0), Math.max(0, colHeight - h)));
    this.stickySpacer.style.height = `${frozen}px`;

    // 2. Swap which edge is held.
    if (this.stickyDir > 0) {
      this.style.top = `${Math.round(vh - h)}px`;
      this.style.bottom = 'auto';
    } else {
      this.style.bottom = `${Math.round(vh - h)}px`;
      this.style.top = 'auto';
    }
  }

  private positionButtonsRelativeToProductCard() {
    // Find the first product card container
    const productCardContainer = this.querySelector('.product-card-container') as HTMLElement;
    if (!productCardContainer) return;

    // Get the height of the product card container
    const containerHeight = productCardContainer.offsetHeight;
    if (containerHeight === 0) return; // Wait for content to have height

    // Get actual button height from CSS (36px mobile, 48px desktop)
    const buttonHeight = this.nextButton?.offsetHeight || this.prevButton?.offsetHeight || 48;
    // Position at vertical center of product card container
    let topPosition = containerHeight / 2 - buttonHeight / 2;

    // On mobile, pull buttons upward to avoid overlapping with text
    if (this.isMobile()) {
      topPosition -= 69;
    }

    // Apply position to buttons
    if (this.prevButton) {
      this.prevButton.style.top = `${topPosition}px`;
    }
    if (this.nextButton) {
      this.nextButton.style.top = `${topPosition}px`;
    }
  }

  handleScroll() {
    if (!this.scrollStarted) {
      this.scrollStarted = true;
    }

    this.updateButtonVisibility();
  }

  private getItemWidth(): number {
    // Use the first child of the flex container as the item reference
    // This works for all carousel types (product cards, refurb steps, etc.)
    const flexContainer = this.firstElementChild as HTMLElement;
    if (!flexContainer) return 0;

    const firstItem = flexContainer.firstElementChild as HTMLElement;
    if (!firstItem) return 0;

    const style = getComputedStyle(flexContainer);
    const gap = parseFloat(style.gap) || parseFloat(style.columnGap) || 0;

    return firstItem.offsetWidth + gap;
  }

  private getVisibleItemCount(): number {
    const itemWidth = this.getItemWidth();
    if (itemWidth === 0) return 1;

    // Calculate how many full items fit in the visible area
    const visibleWidth = this.clientWidth;
    const fullItemsVisible = Math.floor(visibleWidth / itemWidth);

    // Return at least 1 item to scroll
    return Math.max(1, fullItemsVisible);
  }

  private scrollByVisibleItems(direction: number) {
    const itemWidth = this.getItemWidth();
    if (itemWidth === 0) return;

    // Scroll by the number of fully visible items
    const itemsToScroll = this.getVisibleItemCount();
    const scrollAmount = itemWidth * itemsToScroll;

    this.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth',
    });
  }

  private updateButtonVisibility() {
    const scrollLeft = this.scrollLeft;
    const maxScrollLeft = this.scrollWidth - this.clientWidth;
    // Use a small tolerance for mobile/touch devices where scroll might not be exact
    const tolerance = 4;
    const hasOverflow = maxScrollLeft > tolerance;
    //const keepBothVisible = this.isMobile();
    const keepBothVisible = false;

    // Left button
    if (this.prevButton) {
      if (keepBothVisible) {
        // On mobile, show if there is overflow
        if (!hasOverflow) {
          this.prevButton.classList.add('hidden');
        } else {
          this.prevButton.classList.remove('hidden');
        }
      } else {
        // Desktop: hide at start or if no overflow
        if (scrollLeft <= tolerance || !hasOverflow) {
          this.prevButton.classList.add('hidden');
        } else {
          this.prevButton.classList.remove('hidden');
        }
      }
    }

    // Right button
    if (this.nextButton) {
      if (keepBothVisible) {
        // On mobile, show if there is overflow
        if (!hasOverflow) {
          this.nextButton.classList.add('hidden');
        } else {
          this.nextButton.classList.remove('hidden');
        }
      } else {
        // Desktop: hide at end or if no overflow
        const atEnd = scrollLeft >= maxScrollLeft - tolerance;
        if (atEnd || !hasOverflow) {
          this.nextButton.classList.add('hidden');
        } else {
          this.nextButton.classList.remove('hidden');
        }
      }
    }
  }
}

if (!customElements.get('horizontal-scroll')) {
  customElements.define('horizontal-scroll', HorizontalScroll);
}
